/**
 * Void path planner — the endless line.
 *
 * Grows an open path segment-by-segment through empty 3D space. No ground, no
 * closure: elevation is unbounded (with a gentle net-down bias so gravity
 * reads), and the only constraint is the shared Occupancy map — the line may
 * cross over, under, and flush alongside its own past, but bands never
 * overlap and it never merges into an old channel.
 *
 * Islands are ANCHORS, not scenery: every `anchorEvery` cells the planner
 * owes the line a landing — a flat run whose cells will carry a cliff-block
 * mass built underneath (islands.ts). The planner claims the island's mass
 * envelope immediately so later clauses (and later segments) route around or
 * under it, and the island builder then works entirely inside its own claim.
 *
 * RNG discipline: one family child per segment (`segment/${i}`), clause
 * children keyed by the cell offset they start at — label-derived, never
 * order-dependent across systems.
 *
 * Elevation bookkeeping mirrors elevation.ts exactly: a slope window's whole
 * delta steps at its start index (interior cells already carry the exit
 * base), which is what realize/assemble expect of reserved windows.
 */
import type { Dir } from "../../shared/types.ts";
import type { Rng } from "../rng.ts";
import type { PieceRegistry } from "../registry.ts";
import {
  CLIMB_VOCAB,
  DESCENT_VOCAB,
  type Reservation,
  type SlopeOption,
  TURN_DESCENTS,
} from "../elevation.ts";
import {
  applyXform,
  DIR_STEP,
  moveLabel,
  opposite,
  turnLeft,
  turnRight,
  windowXform,
} from "../geom.ts";
import { HELIX_LOOP_SPECS } from "../topology.ts";
import { CHANNEL_CLEARANCE, type OccView } from "./occupancy.ts";

/** Planner dead-end — the caller retries with a fresh rng label on a clean
 * occupancy transaction. Deterministic per seed (dead ends are, too). */
export class PlannerStuck extends Error {}

/**
 * The pocket guard: from `pose`, some flat continuation of `depth` cells
 * must exist (every clause's first cell needs its flat band, so a head with
 * no flat continuation is one clause away from stuck). `alsoBlocked`
 * carries cells a pending-but-uncommitted clause would claim. The world
 * generator reuses this after realization: WFC footprint extras land after
 * planning, and a segment only commits if its exit corridor survived them.
 */
export function hasFlatContinuation(
  occ: OccView,
  pose: HeadPose,
  depth: number,
  alsoBlocked: ReadonlySet<string> = new Set(),
): boolean {
  if (
    alsoBlocked.has(key(pose.x, pose.z)) ||
    !occ.isFree(pose.x, pose.z, pose.y, pose.y + CHANNEL_CLEARANCE)
  ) return false;
  if (depth <= 1) return true;
  for (const dirOut of [pose.dir, turnLeft(pose.dir), turnRight(pose.dir)]) {
    const next: HeadPose = {
      x: pose.x + DIR_STEP[dirOut][0],
      z: pose.z + DIR_STEP[dirOut][1],
      y: pose.y,
      dir: dirOut,
    };
    if (hasFlatContinuation(occ, next, depth - 1, alsoBlocked)) return true;
  }
  return false;
}

export interface OpenCell {
  x: number;
  z: number;
  dirIn: Dir;
  dirOut: Dir;
  move: "F" | "L" | "R";
  /** Entry base height (world y — may be any multiple of 0.25). */
  baseY: number;
}

/** Pose entering a cell: the cell's coordinates and the heading into it. */
export interface HeadPose {
  x: number;
  z: number;
  y: number;
  dir: Dir;
}

export interface IslandCrossing {
  /** World-unique island id (rng family `island/${id}`, claim ref `isle/${id}`). */
  id: number;
  /** Local index range of cells riding the island top. */
  start: number;
  len: number;
  /** Island surface height at the run's ENTRY (sloped runs terrace from
   * here — see `profile`). */
  topY: number;
  /** Terrace top per run cell (floor base — a slope window's cells share
   * its LOW base; the ramp bridges). Flat runs: all topY. */
  profile: number[];
  /** Blob margin (Chebyshev rings around the run) the mass may fill. */
  margin: number;
  /** Raised viaduct (USER: pillars vary the island↔track connection):
   * the deck sits this far BELOW the track and kit pier stacks carry
   * the channel across (assemble emits them on the planner's deck
   * record). Absent = the classic flush terrace. */
  raised?: number;
  /** Extra channel cells beside the run (a diamond's branch columns) —
   * the island builder treats them as track: full mass, no caps, no
   * decor. */
  branchCells?: readonly (readonly [number, number])[];
  /** Wide-run flank columns (the wide profile bulges into them) — track
   * patch like branch columns: full mass, no caps, no decor. */
  wideFlanks?: readonly (readonly [number, number])[];
  /** Furniture ledges beside flat straight run cells, claimed at plan
   * time; furnished post-realize (stub siding or fan) when the host
   * cell realizes as a plain straight. `host` is the absolute cell
   * index; `side` is L/R of the flow. */
  sideCells?: readonly {
    cell: readonly [number, number];
    host: number;
    side: "L" | "R";
  }[];
  /** Envelope cells that received a MASS claim, with their terrace tops
   * — the planner's own deck record. Assemble consults it for solid
   * support; a third mirror of the terrace rule would risk drift. */
  deckCells?: readonly (readonly [number, number, number])[];
}

/** Planner-placed physics zone (cell-indexed): the set-piece channel.
 * "boost" scales the assist's target speed (approaches, chutes); "jump"
 * marks a gap cell — its realized piece is dropped from the placements and
 * the collision proxies emit no channel there (ballistic flight, priced by
 * trajectory math at plan time); "propeller" is the 2-cell gap variant
 * flown under 0.35 gravity (Marble Blast helicopter); "shock" overrides
 * the marble restitution at hard landings (chute exits). */
export interface PlanMod {
  start: number;
  len: number;
  kind:
    | "boost"
    | "jump"
    | "propeller"
    | "shock"
    | "icy"
    | "gravity"
    | "spur"
    | "reorient"
    | "braid";
  /** Reorient only: signed roll (radians) about the run's travel axis —
   * everything downstream presents rotated (wall/ceiling riding). */
  angle?: number;
}

/** Takeable spur drop (M13's loop-mode takeable.ts wired into the void):
 * a 1-cell straight host whose side cell carries an end-hole diving
 * board, with the MAIN line hooking down to pass back DIRECTLY UNDER
 * that cell at Δ exactly 1.0 (the bridge law). world.ts mutates the host
 * placement straight → split, adds the board, and hangs the one-way alt
 * centerline (out over the hole) on the host window. */
export interface SpurDrop {
  /** Cell index of the split host (its window is pinned to 1 cell). */
  host: number;
  /** Cell index where the main line passes back under the branch cell —
   * the receiver the falling marble lands on (same coords as the branch
   * cell, baseY exactly host − 1.0). */
  receiver: number;
  /** World dir from the host toward the branch cell. */
  dir: Dir;
  /** The diving-board piece on the branch cell. */
  cap: "end-hole-rounded" | "end-hole-square";
}

/** Braided path (M17): the line forks into two genuine lanes that wander
 * apart and merge back `len` cells later. The MAIN lane is `len` straight
 * cells of the segment walk; cell `start` is the FORK host and cell
 * `start + len - 1` the MERGE host (both pinned 1-cell straights —
 * world.ts mutates them straight → split, spur-style). The ALT lane is a
 * real OpenCell sub-walk beside the main lane (offset 1..breadth on
 * `side`), NOT part of plan.cells — it realizes as its own sub-skeleton
 * and hangs on the fork window's LoopSegment.alt. */
export interface Braid {
  /** Cell index of the fork host. */
  start: number;
  /** Main-lane length in cells (merge host = start + len − 1). */
  len: number;
  /** World dir from the main lane toward the alt side. */
  side: Dir;
  /** The alt lane as a real adjacent-cell walk, fork branch cell →
   * merge branch cell, flat at the fork's baseY. */
  altCells: OpenCell[];
  /** Lane character (M19b): surface contrast between the lanes — TAGS
   * ONLY (rail-station modifiers + pipe recolor). The tag-only law:
   * laneMods never pin windows, never join plan.mods (realize's exclude
   * rules must not see them), never alter geometry. Both lanes exclude
   * tags within 2 cells of fork and merge (the junction-lip law in
   * friction form — an icy patch at a merge mouth parks a slow rider on
   * the lip). */
  laneMods?: { main?: "icy" | "boost"; alt?: "icy" | "boost" };
  /** Wanderer character (M19b): the alt lane leaned into the far
   * offsets this draw — always compensated by an alt boost tag so the
   * long way around is never a strict loser. */
  wanderer?: boolean;
  /** OPEN fork (V3, user directive): this braid runs to the SEGMENT
   * BOUNDARY without merging — the alt lane exits forward beside the
   * main lane and the SUCCESSOR segment must plan the continuation
   * (HeadState.openBraid; the successor-probe gate is what makes
   * leaving it open safe: an open fork only commits when its resolution
   * actually plans). No merge host, no merge mutation this segment. */
  open?: boolean;
  /** Continuation (V3): this braid RESOLVES a fork left open by the
   * previous segment — the alt lane enters at the boundary (no fork
   * host) and merges at `start + len − 1`. */
  cont?: boolean;
}

/** An alt lane crossing a segment boundary (V3). Rides HeadState. */
export interface OpenBraidState {
  /** World dir from the main lane toward the alt side. */
  side: Dir;
  /** Alt-lane offset at the boundary (1..3). */
  offset: number;
  /** Main-lane cells covered since the fork (the resolution cap counts
   * against this — "up to a point"). */
  cells: number;
  laneMods?: Braid["laneMods"];
  wanderer?: boolean;
}

/** Hard cap on a fork's total main-lane span (fork → merge), cells.
 * "Up to a point": a continuation may STAY OPEN once (M23 braid
 * networks — the alt lane rides a whole middle segment), so the longest
 * legal chain is fork segment + one full open segment + merge segment. */
export const OPEN_BRAID_CAP = 60;
/** A continuation may re-emit the open state at this chance when the
 * cap still affords the full middle segment plus a minimal merge. */
const STAY_OPEN_CHANCE = 0.35;

/** M21 — climate → hazard appetite (clause-weight multipliers by the
 * zone's BASE mood). Legibility rule: each mood leans at most 2-3
 * clauses, hard enough to FEEL across a 5-segment zone. */
export const MOOD_CLAUSE_BIAS: Record<string, Record<string, number>> = {
  snowdrift: { icy: 2.5, sprint: 0.7 },
  goldhour: { jump: 1.6, sprint: 1.4, icy: 0.5 },
  ember: { sprint: 1.5, cornerDrop: 1.3 },
  abyss: { gravity: 1.8, icy: 1.3 },
  midnight: { braid: 1.5, spurDrop: 1.4 },
  violet: { helixDive: 1.7, reorient: 1.3 },
};

export interface SegmentPlan {
  index: number;
  cells: OpenCell[];
  /** Movement set-piece zones (M11): e.g. boost runs into big chutes. */
  mods: PlanMod[];
  /** Takeable spur drops (local host/receiver cell indices). */
  spurs: SpurDrop[];
  /** Braided paths (fork/merge host cell indices + the alt sub-walk). */
  braids: Braid[];
  /** Slope/set-piece windows (local indices) — realize commits them as-is. */
  reservations: Reservation[];
  crossings: IslandCrossing[];
  entry: HeadPose;
  exit: HeadPose;
}

/** Carried head-of-line state between segments (pose + anchor cadence). */
export interface HeadState {
  pose: HeadPose;
  /** Cells travelled since the line last left an island. */
  sinceAnchor: number;
  /** Island due when sinceAnchor reaches this. */
  anchorEvery: number;
  /** Monotone island counter — ids must be world-unique. */
  islandCount: number;
  /** A fork still unresolved at this boundary (V3) — the next segment
   * MUST open with its continuation and merge within the cap. */
  openBraid?: OpenBraidState;
}

/** The line spawns anchored: segment 0 opens with its home island due. */
export function initialHeadState(): HeadState {
  return {
    pose: { x: 0, z: 0, y: 0, dir: 0 },
    sinceAnchor: 0,
    anchorEvery: 0,
    islandCount: 0,
  };
}

/** Segment length floor — clauses run until at least this many cells. */
const SEGMENT_TARGET = 26;
/** Deepest mass an island envelope reserves below its top. */
export const ISLAND_MAX_DEPTH = 3.25;
/** Airspace above island surface cells reserved for vegetation, in TRACK
 * units — tall decor only grows where this claim succeeded. Nature trees
 * run to ~1.8 WORLD units = 0.9 track units at the slalom scale; 1.0
 * leaves margin. */
export const CANOPY_HEIGHT = 1.0;
/** Void-flight cells between islands (drawn per departure). */
const ANCHOR_EVERY_MIN = 14;
const ANCHOR_EVERY_MAX = 38;

const key = (x: number, z: number) => `${x},${z}`;

interface Step {
  move: "F" | "L" | "R";
  /** Band this cell claims, relative to its entry base: [lo, hi]. */
  band: readonly [number, number];
  /** Entry base delta applied AFTER this cell (step-at-start convention:
   * a window's whole delta lands after its first cell). */
  stepAfter?: number;
}

export function planSegment(
  rng: Rng,
  index: number,
  state: HeadState,
  occ: OccView,
  registry: PieceRegistry,
  braidBias = 1,
  /** Merged clause-weight bias (M21 climate × M24 temperament) — the
   * caller (world.ts) composes it; {} = neutral (finite-mode callers,
   * tests). */
  clauseBias: Record<string, number> = {},
): { plan: SegmentPlan; next: HeadState } {
  return new SegmentBuilder(
    rng,
    index,
    state,
    occ,
    registry,
    braidBias,
    clauseBias,
  ).build();
}

class SegmentBuilder {
  private cells: OpenCell[] = [];
  private reservations: Reservation[] = [];
  private mods: PlanMod[] = [];
  private spurs: SpurDrop[] = [];
  private braids: Braid[] = [];
  private crossings: IslandCrossing[] = [];
  private pose: HeadPose;
  private sinceAnchor: number;
  private anchorEvery: number;
  private islandCount: number;
  private readonly ref: string;
  private readonly entry: HeadPose;

  /** Fork left open by the PREVIOUS segment (V3) — resolved first. */
  private readonly openBraidIn?: OpenBraidState;
  /** Fork THIS segment leaves open at its boundary (V3). */
  private openBraidOut?: OpenBraidState;

  constructor(
    private readonly rng: Rng,
    private readonly index: number,
    state: HeadState,
    private readonly occ: OccView,
    private readonly registry: PieceRegistry,
    /** Per-seed breadth knob (1..3, drawn once by the world from
     * rngFamily(seed, "braid-breadth")) — some seeds are tangly, some
     * clean. Caps how far a braid's alt lane may wander out. */
    private readonly braidBias: number = 1,
    /** Merged clause bias (climate × temperament). */
    private readonly clauseBias: Record<string, number> = {},
  ) {
    this.pose = { ...state.pose };
    this.sinceAnchor = state.sinceAnchor;
    this.anchorEvery = state.anchorEvery;
    this.islandCount = state.islandCount;
    this.ref = `seg/${index}`;
    this.entry = { ...state.pose };
    this.openBraidIn = state.openBraid;
  }

  build(): { plan: SegmentPlan; next: HeadState } {
    // V3: a fork left open at the previous boundary resolves FIRST — the
    // continuation is not a clause the planner may skip. Failure is a
    // PlannerStuck (the retry/probe machinery re-attempts; the fork only
    // committed because a successor probe planned this very
    // continuation, so a viable label exists).
    if (this.openBraidIn) {
      if (!this.tryBraidContinuation(this.rng.child("braid-cont"))) {
        throw new PlannerStuck(
          `segment ${this.index}: open braid continuation failed ` +
            `(offset ${this.openBraidIn.offset}, ` +
            `${this.openBraidIn.cells} cells since fork)`,
        );
      }
    }
    while (this.cells.length < SEGMENT_TARGET) {
      const at = this.cells.length;
      const clauseRng = this.rng.child(`clause/${at}`);
      if (this.sinceAnchor >= this.anchorEvery) {
        if (this.tryIsland(clauseRng)) continue;
        // No room for an island here — fly on and retry next clause.
        if (this.tryVoidClause(clauseRng.child("fallback"))) continue;
      } else if (this.tryVoidClause(clauseRng)) {
        continue;
      }
      // Fully stuck: escape with a steep descent, the void below is the
      // least-travelled direction (net drift is downward).
      if (!this.tryEscape(clauseRng.child("escape"))) {
        throw new PlannerStuck(
          `segment ${this.index}, cell ${at}, pose ${
            JSON.stringify(this.pose)
          }`,
        );
      }
    }
    // Boundary margin: end the segment inside a straight so the WFC seam
    // between segments always sits mid-run. Keep flying normal clauses
    // until a 2-cell straight fits. An OPEN braid (V3) already ran its
    // straight main lane flush to the boundary — its tail IS the margin,
    // and appending more cells would strand the alt lane short of the
    // seam.
    let guard = 0;
    while (!this.openBraidOut && !this.commitSteps(this.straightSteps(2))) {
      if (guard++ > 40) {
        throw new PlannerStuck(`closing segment ${this.index}`);
      }
      const rng = this.rng.child(`boundary/${this.cells.length}`);
      if (this.tryVoidClause(rng)) continue;
      if (!this.tryEscape(rng.child("escape"))) {
        throw new PlannerStuck(`closing segment ${this.index}`);
      }
    }

    // Reorient window veto: the twist's presentation field is a pure
    // axial blend across an infinite slab (frame.ts twistParam) — any
    // OTHER geometry of this segment inside the open window would
    // present mid-roll: continuous within the segment, but the boundary
    // cells then carry a partial roll against the neighbor segment's
    // exact entry/exit frame and the world tears at the seam (a marble
    // fell through one). The whole attempt is discarded; the caller
    // retries under a fresh label.
    if (this.twistWindow) {
      const { px, pz, dx, dz, start, len } = this.twistWindow;
      const bad = (x: number, z: number): void => {
        const a = (x - px) * dx + (z - pz) * dz;
        if (a > 0.01 && a < len - 0.01) {
          throw new PlannerStuck(
            `segment ${this.index}: geometry at (${x},${z}) inside the ` +
              `reorient window`,
          );
        }
      };
      for (const [i, c] of this.cells.entries()) {
        if (i >= start && i < start + len) continue;
        bad(c.x, c.z);
      }
      for (const b of this.braids) {
        for (const c of b.altCells) bad(c.x, c.z);
      }
      for (const s of this.spurs) {
        const c = this.cells[s.host];
        const [wx, wz] = DIR_STEP[s.dir];
        bad(c.x + wx, c.z + wz);
      }
      for (const cr of this.crossings) {
        for (const [x, z] of cr.deckCells ?? []) bad(x, z);
        for (const [x, z] of cr.branchCells ?? []) bad(x, z);
        for (const [x, z] of cr.wideFlanks ?? []) bad(x, z);
        for (const sc of cr.sideCells ?? []) bad(sc.cell[0], sc.cell[1]);
      }
    }

    const plan: SegmentPlan = {
      index: this.index,
      cells: this.cells,
      mods: this.mods,
      spurs: this.spurs,
      braids: this.braids,
      reservations: this.reservations,
      crossings: this.crossings,
      entry: this.entry,
      exit: { ...this.pose },
    };
    return {
      plan,
      next: {
        pose: { ...this.pose },
        sinceAnchor: this.sinceAnchor,
        anchorEvery: this.anchorEvery,
        islandCount: this.islandCount,
        ...(this.openBraidOut ? { openBraid: this.openBraidOut } : {}),
      },
    };
  }

  // -------------------------------------------------------------------------
  // Clause machinery
  // -------------------------------------------------------------------------

  /** Walk `steps` from the current pose; returns claimed cells + exit pose,
   * or null when any band collides. Commits nothing. */
  private probe(
    steps: readonly Step[],
  ):
    | {
      cells: { x: number; z: number; y: number }[];
      exit: HeadPose;
    }
    | null {
    let { x, z, y, dir } = this.pose;
    const cells: { x: number; z: number; y: number }[] = [];
    // Bands this walk would claim — a clause must not intersect ITSELF
    // either (a turny island run can loop back before anything commits).
    const pending = new Map<string, [number, number][]>();
    for (const s of steps) {
      const dirOut = s.move === "F"
        ? dir
        : s.move === "L"
        ? turnLeft(dir)
        : turnRight(dir);
      const y0 = y + s.band[0];
      const y1 = y + s.band[1];
      if (!this.occ.isFree(x, z, y0, y1)) return null;
      const k = key(x, z);
      for (const [p0, p1] of pending.get(k) ?? []) {
        if (y0 < p1 && p0 < y1) return null;
      }
      const bands = pending.get(k) ?? [];
      bands.push([y0, y1]);
      pending.set(k, bands);
      cells.push({ x, z, y });
      y += s.stepAfter ?? 0;
      dir = dirOut;
      x += DIR_STEP[dirOut][0];
      z += DIR_STEP[dirOut][1];
    }
    return { cells, exit: { x, z, y, dir } };
  }

  /** Probe + pocket guard + append cells + claim bands + advance the pose. */
  private commitSteps(steps: readonly Step[]): boolean {
    const probed = this.probe(steps);
    if (!probed) return false;
    const pending = new Set(probed.cells.map((p) => key(p.x, p.z)));
    if (!hasFlatContinuation(this.occ, probed.exit, 3, pending)) return false;
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i];
      const p = probed.cells[i];
      const dirIn = this.pose.dir;
      const dirOut = s.move === "F"
        ? dirIn
        : s.move === "L"
        ? turnLeft(dirIn)
        : turnRight(dirIn);
      this.cells.push({
        x: p.x,
        z: p.z,
        dirIn,
        dirOut,
        move: s.move,
        baseY: p.y,
      });
      this.occ.claim(p.x, p.z, p.y + s.band[0], p.y + s.band[1], this.ref);
      this.pose = {
        x: p.x + DIR_STEP[dirOut][0],
        z: p.z + DIR_STEP[dirOut][1],
        y: p.y + (s.stepAfter ?? 0),
        dir: dirOut,
      };
      this.sinceAnchor++;
    }
    return true;
  }

  private straightSteps(len: number): Step[] {
    return Array.from({ length: len }, () => ({
      move: "F" as const,
      band: [0, CHANNEL_CLEARANCE] as const,
    }));
  }

  // -------------------------------------------------------------------------
  // Void-flight clauses
  // -------------------------------------------------------------------------

  private tryVoidClause(rng: Rng): boolean {
    // M21+M24: climate × temperament lean the hazard appetite — one
    // weighted draw either way (stream shape identical, outcomes lean).
    // Snowdrift zones grow icy patches; a treacherous SEED grows them
    // everywhere.
    const bias = this.clauseBias;
    const kind = rng.weighted(
      ([
        ["straight", 4],
        ["turn", 2.5],
        ["jog", 1.5],
        ["slope", 3],
        ["cornerDrop", 1.2],
        ["jump", 0.8],
        ["sprint", 0.9],
        ["helixDive", 0.5],
        ["icy", 0.7],
        ["gravity", 0.5],
        ["spurDrop", 0.35],
        ["reorient", 0.45],
        ["braid", 0.5],
      ] as const).map(([k, w]) => [k, w * (bias[k] ?? 1)] as const),
    );
    const attempts: (() => boolean)[] = [];
    switch (kind) {
      case "straight":
        attempts.push(() =>
          this.commitSteps(this.straightSteps(rng.int(2, 6)))
        );
        // Inch forward: a 1-cell step navigates any corridor the pocket
        // guard has promised, one clause at a time.
        attempts.push(() => this.commitSteps(this.straightSteps(1)));
        break;
      case "turn": {
        const first = rng.chance(0.5) ? "L" : "R";
        for (const m of first === "L" ? ["L", "R"] : ["R", "L"]) {
          attempts.push(() =>
            this.commitSteps([
              { move: m as "L" | "R", band: [0, CHANNEL_CLEARANCE] },
            ])
          );
        }
        break;
      }
      case "jog": {
        // "FRL"/"FLR" jogs — the staircase signature the short s-curve
        // pieces (and wiggle composites) collapse onto.
        const first = rng.chance(0.5) ? ["F", "R", "L"] : ["F", "L", "R"];
        const alt = first[1] === "R" ? ["F", "L", "R"] : ["F", "R", "L"];
        for (const moves of [first, alt]) {
          attempts.push(() =>
            this.commitSteps(
              moves.map((m) => ({
                move: m as Step["move"],
                band: [0, CHANNEL_CLEARANCE] as const,
              })),
            )
          );
        }
        break;
      }
      case "slope":
        return this.trySlope(rng);
      case "cornerDrop":
        return this.tryCornerDrop(rng);
      case "jump":
        return this.tryJump(rng);
      case "sprint":
        return this.trySprint(rng);
      case "helixDive":
        return this.tryHelixDive(rng);
      case "icy":
        return this.tryIcy(rng);
      case "gravity":
        return this.tryGravity(rng);
      case "spurDrop":
        return this.trySpurDrop(rng);
      case "reorient":
        return this.tryReorient(rng);
      case "braid":
        return this.tryBraid(rng);
    }
    for (const attempt of attempts) if (attempt()) return true;
    return false;
  }

  /** Straight-run slope from the shared vocab. Descents dominate (the void
   * drift is downward); climbs keep the rhythm honest. */
  private trySlope(rng: Rng): boolean {
    const climb = rng.chance(0.18);
    const vocab = climb ? CLIMB_VOCAB : DESCENT_VOCAB;
    const delta = rng.weighted(
      (climb
        ? [["0.5", 4], ["1", 2]]
        : [["0.5", 3], ["1", 2.5], ["1.5", 2]]) as readonly (readonly [
          string,
          number,
        ])[],
    );
    const options = vocab[delta];
    const option: SlopeOption = rng.pick(options);
    return this.commitSlope(option, climb);
  }

  private commitSlope(option: SlopeOption, climb: boolean): boolean {
    // Build the step list part-by-part; each part is its own reservation
    // (multi-part options chain, descents traverse parts in reverse —
    // mirroring elevation.ts reserve()).
    const parts = climb ? option.parts : option.parts.slice().reverse();
    const steps: Step[] = [];
    for (const part of parts) {
      const signed = climb ? part.delta : -part.delta;
      for (let j = 0; j < part.len; j++) {
        // Every cell of the part claims the part's FULL envelope. The
        // whole delta rides on cell 0's stepAfter, so later cells sit at
        // the part's EXIT y — their band must reach BACK across the delta
        // (the piece descends/climbs continuously through them). The old
        // j>0 band repeated cell 0's, leaving the geometry's upper half
        // unclaimed on descents: apfuzz-81's slant-long-d walls topped out
        // 1.0 above their claim and barricaded the channel crossing above.
        const lo = j === 0 ? Math.min(0, signed) : Math.min(0, -signed);
        const hi = (j === 0 ? Math.max(0, signed) : Math.max(0, -signed)) +
          CHANNEL_CLEARANCE;
        steps.push({
          move: "F",
          band: [lo, hi],
          ...(j === 0 ? { stepAfter: signed } : {}),
        });
      }
    }
    const start = this.cells.length;
    if (!this.commitSteps(steps)) return false;
    // BOOST-CHUTE set-piece: big descents get a boost zone from two cells
    // ahead of the lip through the chute — the spectacle reads as the
    // track hurling the marble (and the exit momentum carries what
    // follows).
    const totalDrop = parts.reduce((a2, p2) => a2 + p2.delta, 0);
    if (!climb && totalDrop >= 1.0) {
      this.mods.push({
        start: Math.max(0, start - 2),
        len: start - Math.max(0, start - 2) + option.totalLen,
        kind: "boost",
      });
    }
    // SHOCK-ABSORBER zone (M11): the two cells after a big chute are the
    // impact valley — the marble arrives with vy piled on; killing the
    // bounce there keeps the landing readable (Marble Blast shock
    // absorber before a hard drop). Cells may not be planned yet; the
    // mod is cell-indexed, so the windows that eventually cover them
    // pick it up.
    if (!climb && totalDrop >= 1.5) {
      this.mods.push({
        start: start + option.totalLen,
        len: 2,
        kind: "shock",
      });
    }
    let at = start;
    for (const part of parts) {
      this.reservations.push({
        start: at,
        len: part.len,
        pieceIds: part.ids,
        reversed: !climb && !part.asIs,
      });
      at += part.len;
    }
    return true;
  }

  /** Turn-window set piece: helix quarters/halves and bare-corner
   * drop-stacks from TURN_DESCENTS — the void's corner descents. */
  private tryCornerDrop(rng: Rng): boolean {
    const pattern = rng.weighted(
      [
        ["L", 1.5],
        ["R", 1.5],
        ["FLF", 3],
        ["FRF", 3],
        ["FFLFF", 1],
        ["FFRFF", 1],
        ["FLFFLF", 0.8],
        ["FRFFRF", 0.8],
      ] as const,
    );
    const spec = TURN_DESCENTS[pattern];
    const def = this.registry.variants.find(
      (v) => v.id === spec.ids[0] && !v.reversed,
    );
    if (!def) return false;

    const steps: Step[] = pattern.split("").map((m, j) => ({
      move: m as Step["move"],
      band: [-spec.delta, CHANNEL_CLEARANCE] as const,
      ...(j === 0 ? { stepAfter: -spec.delta } : {}),
    }));

    // Footprint extras (arc interiors) must be free too — reservations are
    // committed unchecked by realize, so the planner is the gatekeeper.
    const probed = this.probe(steps);
    if (!probed) return false;
    const first = probed.cells[0];
    const xf = windowXform(first.x, first.z, this.pose.dir, 0);
    const windowSet = new Set(probed.cells.map((p) => key(p.x, p.z)));
    const extras: [number, number][] = [];
    for (const [fx, fz] of def.footprint) {
      const w = applyXform(xf, [fx, 0, fz]);
      const wx = Math.round(w[0]);
      const wz = Math.round(w[2]);
      if (windowSet.has(key(wx, wz))) continue;
      if (
        !this.occ.isFree(
          wx,
          wz,
          first.y - spec.delta,
          first.y + CHANNEL_CLEARANCE,
        )
      ) return false;
      extras.push([wx, wz]);
    }

    const start = this.cells.length;
    if (!this.commitSteps(steps)) return false;
    for (const [wx, wz] of extras) {
      this.occ.claim(
        wx,
        wz,
        first.y - spec.delta,
        first.y + CHANNEL_CLEARANCE,
        this.ref,
      );
    }
    this.reservations.push({
      start,
      len: pattern.length,
      pieceIds: spec.ids,
      reversed: false,
    });
    return true;
  }

  /**
   * SPRINT set-piece (M11): a long straight boost zone on the flat — the
   * marble accelerates to the boost target and tears down the run. The
   * zone stops 2 cells short of the clause end so the brake has runway
   * before whatever follows. These are ALSO the only cells where WFC may
   * free-collapse BUMP windows (4 flat cells fully inside a boost zone):
   * at sprint speed a bump crest is a small launch, and the ribbon that
   * follows the bump profile is its own catch.
   */
  private trySprint(rng: Rng): boolean {
    const len = rng.int(6, 8);
    const start = this.cells.length;
    if (!this.commitSteps(this.straightSteps(len))) return false;
    this.mods.push({ start, len: len - 2, kind: "boost" });
    return true;
  }

  /**
   * JUMP set-piece (M11): a real 1-cell channel gap. Layout is
   * boost-approach (3 flat cells — the assist accelerates to launch
   * speed) → gap cell (reserved slant-b DESCENDING 0.5; world.ts drops
   * its placement so nothing renders and the proxies emit no channel —
   * absence IS the gap) → 2 flat landing cells 0.5 lower.
   *
   * Trajectory, priced here at plan time: launch is horizontal at the
   * boost target v ≈ 3.2·1.9·TRACK_SCALE-adjusted ≈ 6.1 world u/s; the
   * 0.5-track drop (1.0 world) takes t = √(2·1.0/20) ≈ 0.316 s, covering
   * v·t ≈ 1.9 world of the 2.0-world gap — the marble lands on the catch
   * at the landing's leading edge (the proxies extend a pan under the lip
   * so slow arrivals are caught too; hot arrivals ≤ the 9 u/s brake cap
   * land within the first landing cell). The gap cell's claim spans down
   * to the landing base — the flight band.
   */
  private tryJump(rng: Rng): boolean {
    const APPROACH = 3;
    const LANDING = 2;
    // PROPELLER variant (2-cell gap flown under 0.35 gravity — same
    // launch speed, longer floatier arc): drop 1.0 world takes
    // √(2/7) ≈ 0.535 s, covering ≈ 3.3 of the 4.0-world gap at the boost
    // target; the catch pan absorbs the shortfall, the brake cap (9 u/s
    // → 4.8 world) stays on the landing cells.
    const propeller = rng.chance(0.4);
    const gapLen = propeller ? 2 : 1;
    const gapIds = propeller ? ["ramp-long-b", "slant-long-b"] : ["slant-b"];
    // The gap window realizes as a reversed 0.5-descent (dropped from the
    // placements later); its centerline keeps the rail continuous through
    // the flight.
    if (
      !this.registry.variants.some((v) =>
        gapIds.includes(v.id) && v.reversed && v.moves.length === gapLen
      )
    ) return false;
    const steps: Step[] = [
      ...this.straightSteps(APPROACH),
      // Gap cells mirror commitSlope's band shape: every cell claims down
      // to the landing base (the flight band), delta steps after cell 0.
      ...Array.from({ length: gapLen }, (_, j) => ({
        move: "F" as const,
        band: [-0.5, CHANNEL_CLEARANCE] as const,
        ...(j === 0 ? { stepAfter: -0.5 } : {}),
      })),
      ...this.straightSteps(LANDING),
    ];
    const start = this.cells.length;
    if (!this.commitSteps(steps)) return false;
    this.mods.push({ start, len: APPROACH, kind: "boost" });
    this.mods.push({
      start: start + APPROACH,
      len: gapLen,
      kind: propeller ? "propeller" : "jump",
    });
    this.reservations.push({
      start: start + APPROACH,
      len: gapLen,
      pieceIds: gapIds,
      reversed: true,
    });
    return true;
  }

  /**
   * FULL-HELIX SPIRAL DIVE (promoted backlog): the loop-mode ensemble
   * dropped into the void — a 360° spiral block whose entry and exit share
   * one mid-face port edge; the line dives 2.0 and passes back UNDER its
   * own approach (the underpass straights). The window's moves REVISIT its
   * first cells, which the generic step machinery can't express (per-visit
   * bands at one cell would self-collide) — so this clause is the
   * duplicate-cell-tolerant path: the ensemble claims each footprint cell
   * ONCE over the whole block depth [−2, clearance] (the spiral occupies
   * its full column — nothing may thread through the block), and
   * this.cells simply carries the revisited coordinates twice at their two
   * bases. Realize matches the reserved def by moves (same as loop mode);
   * the mesh provides both levels, and the centerline's spiral arc drops
   * linearly in angle. One per segment — it's a set-piece, not a texture.
   */
  private hadHelixDive = false;
  private tryHelixDive(rng: Rng): boolean {
    if (this.hadHelixDive) return false;
    const large = rng.chance(0.35);
    const turn = rng.chance(0.5) ? "R" : "L";
    const spec = HELIX_LOOP_SPECS.find(
      (s) => s.large === large && s.turn === turn,
    );
    if (!spec) return false;
    const def = this.registry.variants.find(
      (v) => v.id === spec.id && !v.reversed,
    );
    if (!def) return false;

    const { x, z, y, dir } = this.pose;
    const xf = windowXform(x, z, dir, 0);
    const block: [number, number][] = [];
    const seen = new Set<string>();
    for (const [fx, fz] of def.footprint) {
      const w = applyXform(xf, [fx, 0, fz]);
      const wx = Math.round(w[0]);
      const wz = Math.round(w[2]);
      const k = key(wx, wz);
      if (seen.has(k)) continue;
      seen.add(k);
      if (!this.occ.isFree(wx, wz, y - 2, y + CHANNEL_CLEARANCE)) return false;
      block.push([wx, wz]);
    }

    // Walk the ensemble's moves for cell bookkeeping and the exit pose.
    // Interior cells carry the exit base (step-at-start: the whole −2
    // lands after the first cell) — exactly what realize expects of a
    // reserved window.
    let cx = x;
    let cz = z;
    let d = dir;
    const walked: OpenCell[] = [];
    for (let j = 0; j < spec.moves.length; j++) {
      const m = spec.moves[j] as "F" | "L" | "R";
      const dirIn = d;
      const dirOut = m === "F" ? d : m === "L" ? turnLeft(d) : turnRight(d);
      walked.push({
        x: cx,
        z: cz,
        dirIn,
        dirOut,
        move: m,
        baseY: j === 0 ? y : y - 2,
      });
      d = dirOut;
      cx += DIR_STEP[dirOut][0];
      cz += DIR_STEP[dirOut][1];
    }
    const exit: HeadPose = { x: cx, z: cz, y: y - 2, dir: d };
    if (!hasFlatContinuation(this.occ, exit, 3, seen)) return false;

    const start = this.cells.length;
    for (const [wx, wz] of block) {
      this.occ.claim(wx, wz, y - 2, y + CHANNEL_CLEARANCE, this.ref);
    }
    this.cells.push(...walked);
    this.sinceAnchor += spec.moves.length;
    this.pose = exit;
    this.reservations.push({
      start,
      len: spec.moves.length,
      pieceIds: [spec.id],
      reversed: false,
    });
    this.hadHelixDive = true;
    return true;
  }

  /**
   * ICY RUN (M13): a flat straight patch of low-grip channel — the assist
   * backs off (rail.ts scales its authority down) so the marble carries
   * whatever momentum it arrived with and slides wide through whatever
   * follows. The zone READS via the client's ice-pale pipe recolor.
   */
  private tryIcy(rng: Rng): boolean {
    const len = rng.int(4, 7);
    const start = this.cells.length;
    if (!this.commitSteps(this.straightSteps(len))) return false;
    this.mods.push({ start, len, kind: "icy" });
    return true;
  }

  /**
   * GRAVITY-ROTATION RUN (M13): a flat straight patch where the WORLD's
   * gravity vector tilts forward along the run (shim gravityDir) — the
   * marble surges down a run that is geometrically flat, and the follow
   * cam leans with it. Sprint-style layout: the zone stops 2 cells short
   * of the clause end, so gravity has snapped back and the assist has
   * brake runway before whatever follows. READS via the client's
   * violet-charged pipe recolor.
   */
  private tryGravity(rng: Rng): boolean {
    const len = rng.int(6, 8);
    const start = this.cells.length;
    if (!this.commitSteps(this.straightSteps(len))) return false;
    this.mods.push({ start, len: len - 2, kind: "gravity" });
    return true;
  }

  /**
   * TAKEABLE SPUR DROP (M13's loop-mode machinery wired into the void):
   * a straight host cell whose side cell carries an end-hole diving
   * board, with the MAIN line hooking down 1.0 and passing back DIRECTLY
   * UNDER that side cell — the receiver at Δ exactly 1.0 below the deck
   * (the bridge law: two stacked channels just clear). A marble riding
   * the branch (respawn variety alternates onto it) rolls out over the
   * hole, falls through, and lands ON ITS OWN LINE (hole semantics, USER
   * RULE: every fall lands on track below — enforced by construction).
   * The continuation IS the main line, so the head walks on normally.
   *
   * Move pattern (branch left shown; right mirrors), deck level y0:
   *
   *     B ← A4          host H straight, then a 0.5 descent, two turns
   *     ↑    ↑           around the block at y0−0.5, a second 0.5
   *  →  H → A2 → A3     descent into B (= the branch cell, at y0−1),
   *  (B above H+left)   and a flat runway cell out.
   *
   * One per segment — punctuation, not texture.
   */
  private hadSpur = false;
  private trySpurDrop(rng: Rng): boolean {
    if (this.hadSpur) return false;
    // The two descents realize as reversed slant-b (the jump clause's
    // own availability check).
    if (
      !this.registry.variants.some((v) =>
        v.id === "slant-b" && v.reversed && v.moves.length === 1
      )
    ) return false;
    const sides: ("L" | "R")[] = rng.chance(0.5) ? ["L", "R"] : ["R", "L"];
    for (const side of sides) {
      if (this.trySpurSide(rng.child(`side/${side}`), side)) return true;
    }
    return false;
  }

  private trySpurSide(rng: Rng, side: "L" | "R"): boolean {
    const steps: Step[] = [
      // Host (the future split junction).
      { move: "F", band: [0, CHANNEL_CLEARANCE] },
      // First 0.5 descent (band shape mirrors commitSlope/tryJump).
      { move: "F", band: [-0.5, CHANNEL_CLEARANCE], stepAfter: -0.5 },
      // Two turns around the block at y0−0.5.
      { move: side, band: [0, CHANNEL_CLEARANCE] },
      { move: side, band: [0, CHANNEL_CLEARANCE] },
      // Second 0.5 descent, arriving under the branch cell.
      { move: "F", band: [-0.5, CHANNEL_CLEARANCE], stepAfter: -0.5 },
      // The receiver: the branch cell's column at deck − 1.0.
      { move: "F", band: [0, CHANNEL_CLEARANCE] },
      // Flat runway so the landing rolls on.
      { move: "F", band: [0, CHANNEL_CLEARANCE] },
    ];
    const probed = this.probe(steps);
    if (!probed) return false;
    const host = probed.cells[0];
    const w = side === "L" ? turnLeft(this.pose.dir) : turnRight(this.pose.dir);
    const [wx, wz] = DIR_STEP[w];
    const bx = host.x + wx;
    const bz = host.z + wz;
    const rec = probed.cells[5];
    // The hook must come back exactly under the branch cell at Δ 1.0 —
    // true by construction; keep the law loud if the pattern drifts.
    if (rec.x !== bx || rec.z !== bz || rec.y !== host.y - 1) return false;
    // The branch deck band is claimed manually (probe only walks the
    // line); it stacks EXACTLY on the receiver's channel claim below.
    if (!this.occ.isFree(bx, bz, host.y, host.y + CHANNEL_CLEARANCE)) {
      return false;
    }
    const start = this.cells.length;
    if (!this.commitSteps(steps)) return false;
    this.occ.claim(bx, bz, host.y, host.y + CHANNEL_CLEARANCE, this.ref);
    // Pin the structural windows: the host must realize as a plain
    // 1-cell straight (its placement mutates straight → split in
    // world.ts), the descents as slant-b, and the receiver as a bare
    // open channel — no arch/wave may realize over the landing.
    this.reservations.push(
      { start, len: 1, pieceIds: ["straight"], reversed: false },
      { start: start + 1, len: 1, pieceIds: ["slant-b"], reversed: true },
      { start: start + 4, len: 1, pieceIds: ["slant-b"], reversed: true },
      { start: start + 5, len: 1, pieceIds: ["straight"], reversed: false },
    );
    // The spur zone marks the host window: rail centering keeps
    // main-line marbles out of the branch mouth and branch riders on
    // the board (proxies key the composite off the alt marker instead).
    this.mods.push({ start, len: 1, kind: "spur" });
    this.spurs.push({
      host: start,
      receiver: start + 5,
      dir: w,
      cap: rng.chance(0.5) ? "end-hole-rounded" : "end-hole-square",
    });
    this.hadSpur = true;
    return true;
  }

  /**
   * BRAIDED PATH (M17, USER FEATURE): the line forks into TWO genuine
   * lanes that wander apart and merge back L cells later — parts of the
   * stream read as a winding tangle, and per-racer lane choice makes the
   * pack visibly split and rejoin. V1 scope: flat (one y), void-only
   * (this is a void clause), one braid per segment, MAIN lane straight,
   * ALT lane wandering beside it.
   *
   * Layout: L = 8..14 straight main cells; cell 0 is the FORK host and
   * cell L−1 the MERGE host, both pinned to 1-cell straight windows
   * (world.ts mutates them straight → split, spur-style). The alt lane
   * is a REAL OpenCell walk beside the main lane at lateral offset
   * o ∈ [1, B] (B from the per-seed breadth knob): F cells while the
   * offset holds, turn PAIRS to shift a lane (each offset change spends
   * two walk cells — diagonal adjacency is impossible by construction).
   * The walk enters from the fork's branch mouth (heading sideways,
   * turning into travel) and leaves turning back INTO the merge's
   * branch mouth aligned with the arriving lane. Alt cells claim their
   * own channel bands; they are a SUB-skeleton on the Braid record, not
   * part of plan.cells.
   */
  private tryBraid(rng: Rng): boolean {
    // M23 braid networks: up to TWO braids per segment (an open fork is
    // necessarily the last — it runs flush to the boundary).
    if (this.braids.length >= 2 || this.openBraidOut) return false;
    // Flat approach runway: a fork at the crest of a climb receives its
    // marbles at near-zero speed and they park on the junction lip
    // (grav-scan-21's alt rider stalled against the deck edge at the top
    // of a 1.0 slant). The 2 cells behind the fork must be plain flat
    // cells at the braid's level — reserved windows (slopes, set-pieces)
    // and level changes veto the clause. Segment starts are safe by
    // construction (the boundary margin is a flat 2-cell straight).
    const at = this.cells.length;
    for (let k = Math.max(0, at - 2); k < at; k++) {
      if (this.cells[k].baseY !== this.pose.y) return false;
    }
    if (
      this.reservations.some((r) => r.start < at && r.start + r.len > at - 2)
    ) return false;
    const L = rng.int(8, 15);
    // Per-braid breadth from the per-seed bias: bias 1 → always lane 1
    // (a clean parallel siding); bias 3 → up to three lanes out.
    const B = Math.min(3, 1 + Math.floor(this.braidBias * rng.float()));
    const sides: ("L" | "R")[] = rng.chance(0.5) ? ["L", "R"] : ["R", "L"];
    for (const side of sides) {
      if (this.tryBraidSide(rng.child(`side/${side}`), side, L, B)) {
        return true;
      }
    }
    return false;
  }

  private tryBraidSide(
    rng: Rng,
    side: "L" | "R",
    L: number,
    B: number,
  ): boolean {
    const d = this.pose.dir;
    const y0 = this.pose.y;
    const [dx, dz] = DIR_STEP[d];
    const w = side === "L" ? turnLeft(d) : turnRight(d);
    const [wx, wz] = DIR_STEP[w];
    // OPEN FORK (V3, user directive): sometimes the fork does NOT
    // resolve this segment — the braid runs flush to the boundary and
    // the successor plans the merge (bounded by OPEN_BRAID_CAP). Only
    // when the run to the boundary is a sane braid length; the drawn L
    // is overridden by that distance.
    const toBoundary = SEGMENT_TARGET - this.cells.length;
    const open = toBoundary >= 6 && toBoundary <= 18 &&
      rng.child("open").chance(0.3);
    if (open) L = toBoundary;
    const steps = this.straightSteps(L);
    const probed = this.probe(steps);
    if (!probed) return false;
    const fork = probed.cells[0];

    // Wanderer character (M19b): drawn from its own child so non-braid
    // streams never move. Breadth stays capped at B — the wanderer only
    // LEANS the same profile range outward, so its claims are never
    // wider than V1 could produce (no new twist-window veto pressure).
    const wanderer = rng.child("character").chance(0.35);
    // Offset profile per main column j = 1..L−2: starts and ends at 1
    // (the fork/merge branch mouths), wanders in [1, B], |Δ| ≤ 1, with
    // the return-to-1 budget enforced ahead of time. pick() and
    // weighted() both consume exactly ONE draw, so non-wanderer braids
    // stay byte-identical — the wanderer bias is THE M19b reshuffle.
    const pRng = rng.child("profile");
    const o = new Array<number>(L).fill(1);
    const wanderEnd = open ? L - 1 : L - 3;
    for (let j = 2; j <= wanderEnd; j++) {
      // An open braid never owes the return-to-1 budget this segment —
      // the continuation walks it home.
      const maxHere = open ? B : Math.min(B, 1 + (L - 2 - j));
      const choices: number[] = [];
      for (const step of [-1, 0, 1]) {
        const v = o[j - 1] + step;
        if (v >= 1 && v <= maxHere) choices.push(v);
      }
      o[j] = wanderer
        ? pRng.weighted(choices.map((c) => [c, c * c] as const))
        : pRng.pick(choices);
    }

    // The alt walk (see the clause comment for the move law).
    const cellAt = (j: number, off: number): readonly [number, number] => [
      fork.x + dx * j + wx * off,
      fork.z + dz * j + wz * off,
    ];
    const altCells: OpenCell[] = [];
    const emit = (j: number, off: number, dirIn: Dir, dirOut: Dir): void => {
      const [x, z] = cellAt(j, off);
      altCells.push({
        x,
        z,
        dirIn,
        dirOut,
        move: moveLabel(dirIn, dirOut),
        baseY: y0,
      });
    };
    // Branch cell beside the fork: enter sideways, turn into travel.
    emit(0, 1, w, d);
    const lastJ = open ? L - 1 : L - 2;
    for (let j = 1; j <= lastJ; j++) {
      const next = j === lastJ ? (open ? o[j] : 1) : o[j + 1];
      if (next === o[j]) {
        emit(j, o[j], d, d);
      } else if (next > o[j]) {
        emit(j, o[j], d, w);
        emit(j, next, w, d);
      } else {
        emit(j, o[j], d, opposite(w));
        emit(j, next, opposite(w), d);
      }
    }
    // Branch cell beside the merge: turn back into the branch mouth.
    // (An open braid has no merge this segment — its last alt cell
    // exits FORWARD across the boundary.)
    if (!open) emit(L - 1, 1, d, opposite(w));

    // Alt claims must be free (disjoint from the main lane by offset ≥ 1
    // construction), and the exit corridor must survive BOTH lanes.
    for (const c of altCells) {
      if (!this.occ.isFree(c.x, c.z, y0, y0 + CHANNEL_CLEARANCE)) {
        return false;
      }
    }
    const pending = new Set([
      ...probed.cells.map((p) => key(p.x, p.z)),
      ...altCells.map((c) => key(c.x, c.z)),
    ]);
    if (!hasFlatContinuation(this.occ, probed.exit, 3, pending)) return false;
    if (open) {
      // BOTH lanes cross the boundary: the alt lane's forward corridor
      // must be plannable too (the successor probe then truly plans it).
      // The corridor pose is the NEXT cell PAST the boundary — the
      // hasFlatContinuation contract (its own first cell must be free).
      const lastAlt = altCells[altCells.length - 1];
      const altExit: HeadPose = {
        x: lastAlt.x + dx,
        z: lastAlt.z + dz,
        y: y0,
        dir: d,
      };
      if (!hasFlatContinuation(this.occ, altExit, 3, pending)) return false;
    }

    const start = this.cells.length;
    if (!this.commitSteps(steps)) return false;
    for (const c of altCells) {
      this.occ.claim(c.x, c.z, y0, y0 + CHANNEL_CLEARANCE, this.ref);
    }
    // Pin fork (+ merge when it resolves here) to plain 1-cell straights
    // (their placements mutate straight → split in world.ts; the
    // junction composites assume a bare straight window). The braid zone
    // marks the junction hosts: rail centering keeps main-lane marbles
    // out of the open branch mouths.
    this.reservations.push(
      { start, len: 1, pieceIds: ["straight"], reversed: false },
    );
    this.mods.push({ start, len: 1, kind: "braid" });
    if (!open) {
      this.reservations.push(
        {
          start: start + L - 1,
          len: 1,
          pieceIds: ["straight"],
          reversed: false,
        },
      );
      this.mods.push({ start: start + L - 1, len: 1, kind: "braid" });
    }
    // Lane mods (M19b): a surface CONTRAST so lane choice matters — an
    // icy lane against a boost lane. Own child stream; tags only (the
    // Braid.laneMods law). A wanderer alt always carries the boost
    // compensation, whatever the contrast draw said.
    const lRng = rng.child("lanes");
    const laneRoll = lRng.float();
    let laneMods: Braid["laneMods"] = laneRoll < 0.5
      ? undefined
      : laneRoll < 0.75
      ? { alt: "icy", main: "boost" }
      : { alt: "boost", main: "icy" };
    if (wanderer) {
      // Compensation trumps the contrast draw; a boost-vs-boost pair is
      // no contrast at all, so the main tag drops in that case.
      laneMods = {
        alt: "boost",
        ...(laneMods?.main && laneMods.main !== "boost"
          ? { main: laneMods.main }
          : {}),
      };
    }
    this.braids.push({
      start,
      len: L,
      side: w,
      altCells,
      ...(laneMods ? { laneMods } : {}),
      ...(wanderer ? { wanderer: true } : {}),
      ...(open ? { open: true } : {}),
    });
    if (open) {
      this.openBraidOut = {
        side: w,
        offset: o[L - 1],
        cells: L,
        ...(laneMods ? { laneMods } : {}),
        ...(wanderer ? { wanderer: true } : {}),
      };
    }
    return true;
  }

  /** BRAID CONTINUATION (V3): resolve the fork the previous segment left
   * open. Runs FIRST — the alt lane enters at the boundary at the
   * carried offset, wanders home, and merges at a pinned straight within
   * the cap. No fork host this segment; the merge is the V1 merge. */
  private tryBraidContinuation(rng: Rng): boolean {
    const ob = this.openBraidIn!;
    const d = this.pose.dir;
    const y0 = this.pose.y;
    const [dx, dz] = DIR_STEP[d];
    const w = ob.side;
    const [wx, wz] = DIR_STEP[w];
    const remaining = OPEN_BRAID_CAP - ob.cells;
    // M23: STAY OPEN — the alt lane rides this whole segment and the
    // NEXT segment merges. Only when the cap affords the full middle
    // segment plus a minimal merge leg downstream.
    if (
      remaining >= SEGMENT_TARGET + 6 &&
      rng.child("stay-open").chance(STAY_OPEN_CHANCE) &&
      this.planContinuation(
        rng.child("open-fit"),
        SEGMENT_TARGET,
        d,
        y0,
        w,
        [dx, dz],
        [wx, wz],
        ob,
        true,
      )
    ) {
      return true;
    }
    const minL = Math.max(6, ob.offset + 4);
    const maxL = Math.max(minL, Math.min(13, remaining));
    // Longest first — a failed probe steps the merge closer (bounded
    // retry inside one attempt, deterministic).
    const want = rng.int(minL, maxL + 1);
    for (let L2 = want; L2 >= minL; L2--) {
      if (this.planContinuation(rng.child(`fit/${L2}`), L2, d, y0, w, [
        dx,
        dz,
      ], [wx, wz], ob)) {
        return true;
      }
    }
    return false;
  }

  private planContinuation(
    rng: Rng,
    L2: number,
    d: Dir,
    y0: number,
    w: Dir,
    [dx, dz]: readonly [number, number],
    [wx, wz]: readonly [number, number],
    ob: OpenBraidState,
    /** M23: leave the chain open at THIS boundary too (no merge). */
    stayOpen = false,
  ): boolean {
    const steps = this.straightSteps(L2);
    const probed = this.probe(steps);
    if (!probed) return false;
    const first = probed.cells[0];

    // Offset profile: o[0] is the carried boundary offset. A merging
    // continuation wanders home under the return budget (o[L2−2] = 1
    // for the merge mouth); a stay-open one keeps wandering to the far
    // boundary.
    const pRng = rng.child("profile");
    const o = new Array<number>(L2).fill(1);
    o[0] = ob.offset;
    const wanderEnd = stayOpen ? L2 - 1 : L2 - 3;
    for (let j = 1; j <= wanderEnd; j++) {
      const maxHere = stayOpen ? 3 : Math.min(3, 1 + (L2 - 2 - j));
      const choices: number[] = [];
      for (const step of [-1, 0, 1]) {
        const v = o[j - 1] + step;
        if (v >= 1 && v <= maxHere) choices.push(v);
      }
      if (choices.length === 0) return false;
      o[j] = ob.wanderer
        ? pRng.weighted(choices.map((c) => [c, c * c] as const))
        : pRng.pick(choices);
    }
    if (!stayOpen) {
      o[L2 - 2] = 1;
      // |Δ| ≤ 1 must hold into the forced return cell too.
      if (L2 >= 3 && Math.abs(o[L2 - 3] - 1) > 1) return false;
    }

    const cellAt = (j: number, off: number): readonly [number, number] => [
      first.x + dx * j + wx * off,
      first.z + dz * j + wz * off,
    ];
    const altCells: OpenCell[] = [];
    const emit = (j: number, off: number, dirIn: Dir, dirOut: Dir): void => {
      const [x, z] = cellAt(j, off);
      altCells.push({
        x,
        z,
        dirIn,
        dirOut,
        move: moveLabel(dirIn, dirOut),
        baseY: y0,
      });
    };
    // The boundary cell continues FORWARD at the carried offset — no
    // fork branch cell (the fork lives segments back).
    const lastJ = stayOpen ? L2 - 1 : L2 - 2;
    for (let j = 0; j <= lastJ; j++) {
      const next = j === lastJ ? (stayOpen ? o[j] : 1) : o[j + 1];
      if (next === o[j]) {
        emit(j, o[j], d, d);
      } else if (next > o[j]) {
        emit(j, o[j], d, w);
        emit(j, next, w, d);
      } else {
        emit(j, o[j], d, opposite(w));
        emit(j, next, opposite(w), d);
      }
    }
    // Branch cell beside the merge: turn back into the branch mouth.
    if (!stayOpen) emit(L2 - 1, 1, d, opposite(w));

    for (const c of altCells) {
      if (!this.occ.isFree(c.x, c.z, y0, y0 + CHANNEL_CLEARANCE)) {
        return false;
      }
    }
    const pending = new Set([
      ...probed.cells.map((p) => key(p.x, p.z)),
      ...altCells.map((c) => key(c.x, c.z)),
    ]);
    if (!hasFlatContinuation(this.occ, probed.exit, 3, pending)) return false;

    const start = this.cells.length;
    if (!this.commitSteps(steps)) return false;
    for (const c of altCells) {
      this.occ.claim(c.x, c.z, y0, y0 + CHANNEL_CLEARANCE, this.ref);
    }
    if (!stayOpen) {
      this.reservations.push(
        {
          start: start + L2 - 1,
          len: 1,
          pieceIds: ["straight"],
          reversed: false,
        },
      );
      this.mods.push({ start: start + L2 - 1, len: 1, kind: "braid" });
    }
    this.braids.push({
      start,
      len: L2,
      side: w,
      altCells,
      cont: true,
      ...(stayOpen ? { open: true } : {}),
      ...(ob.laneMods ? { laneMods: ob.laneMods } : {}),
      ...(ob.wanderer ? { wanderer: true } : {}),
    });
    if (stayOpen) {
      this.openBraidOut = {
        side: w,
        offset: o[L2 - 1],
        cells: ob.cells + L2,
        ...(ob.laneMods ? { laneMods: ob.laneMods } : {}),
        ...(ob.wanderer ? { wanderer: true } : {}),
      };
    }
    return true;
  }

  /**
   * GRAVITY REORIENTATION RUN (M16 — the real gravity feature, USER
   * HIGHLIGHT): a straight void run where the whole stream ROLLS about
   * its travel axis by ±90° or 180° — every island, channel and marble
   * downstream presents rotated in world space (wall/ceiling riding).
   * Plan space never knows: generation, occupancy and the control laws
   * stay +y-up; world.ts composes the presentation frame and the client
   * conjugates marble state through it. Here we only walk the straight
   * run with a FAT claim band (the rolling channel sweeps its walls
   * around the centerline) and mark the window. The kit pieces over the
   * run are dropped (world.ts) — the twist renders as the collision
   * ribbon itself, corkscrewing between the two frames.
   */
  private hadReorient = false;
  /** Committed reorient window (track units): entry-port pivot + axis +
   * cell range. build() vetoes any OTHER geometry of this segment that
   * projects axially into the open window — the presentation field is a
   * pure axial blend (frame.ts twistParam), so anything else inside the
   * slab would present mid-roll and tear a seam at the segment
   * boundaries (grav-scan-13's post-twist spur hook did exactly that). */
  private twistWindow:
    | {
      px: number;
      pz: number;
      dx: number;
      dz: number;
      start: number;
      len: number;
    }
    | null = null;
  private tryReorient(rng: Rng): boolean {
    // One per segment (the presentation frame carries a single twist);
    // hero spawn runway: never open the very first segment mid-roll.
    if (this.hadReorient) return false;
    if (this.index === 0 && this.cells.length < 6) return false;
    const angle = rng.weighted(
      [
        [Math.PI / 2, 2],
        [-Math.PI / 2, 2],
        [Math.PI, 1],
      ] as const,
    );
    // Roll rate stays gentle: 90° over 8 cells, 180° over 12.
    const len = Math.abs(angle) > Math.PI / 2 + 1e-6 ? 12 : 8;
    const steps: Step[] = Array.from({ length: len }, () => ({
      move: "F" as const,
      // The rotating cross-section sweeps wall tips (±0.46 track) above
      // AND below the floor line — claim the swept drum, not just the
      // channel band.
      band: [-0.75, CHANNEL_CLEARANCE] as const,
    }));
    const probed = this.probe(steps);
    if (!probed) return false;
    // FLANK COLUMNS: the roll's swept drum reaches laterally past the
    // cell (wall tips ~±0.72 track mid-roll), and the frame law's roll
    // blend owns the run's corridor (frame.ts TWIST_LAT_MAX) — claim
    // both flanks of every run cell so no other line rides beside the
    // roll. A later clause doubling back through a flank projected
    // axially INTO the twist window and its channel presented mid-roll,
    // tearing a world seam a marble fell through (grav-scan-13).
    const flanks: [number, number][] = [];
    for (const p of probed.cells) {
      for (const side of [turnLeft(this.pose.dir), turnRight(this.pose.dir)]) {
        const [sx, sz] = DIR_STEP[side];
        const fx = p.x + sx;
        const fz = p.z + sz;
        if (!this.occ.isFree(fx, fz, p.y - 0.75, p.y + CHANNEL_CLEARANCE)) {
          return false;
        }
        flanks.push([fx, fz]);
      }
    }
    const pendingBlock = new Set([
      ...probed.cells.map((p) => key(p.x, p.z)),
      ...flanks.map(([fx, fz]) => key(fx, fz)),
    ]);
    if (!hasFlatContinuation(this.occ, probed.exit, 3, pendingBlock)) {
      return false;
    }
    const dir = this.pose.dir;
    const [ddx, ddz] = DIR_STEP[dir];
    const start = this.cells.length;
    if (!this.commitSteps(steps)) return false;
    const y0 = probed.cells[0].y;
    for (const [fx, fz] of flanks) {
      this.occ.claim(fx, fz, y0 - 0.75, y0 + CHANNEL_CLEARANCE, this.ref);
    }
    this.twistWindow = {
      px: probed.cells[0].x - ddx / 2,
      pz: probed.cells[0].z - ddz / 2,
      dx: ddx,
      dz: ddz,
      start,
      len,
    };
    this.mods.push({ start, len, kind: "reorient", angle });
    // Pin every run cell to a bare straight: the twist ribbon assumes
    // the centerline — no wave/arch/funnel may realize under the roll.
    for (let j = 0; j < len; j++) {
      this.reservations.push({
        start: start + j,
        len: 1,
        pieceIds: ["straight"],
        reversed: false,
      });
    }
    this.hadReorient = true;
    return true;
  }

  /** Last resort when boxed in: steep 1.5 chute straight down and out. */
  private tryEscape(rng: Rng): boolean {
    for (const delta of ["1.5", "1", "0.5"]) {
      for (const option of DESCENT_VOCAB[delta]) {
        if (this.commitSlope(option, false)) return true;
      }
    }
    // Try turning first, then dive.
    for (const m of rng.chance(0.5) ? ["L", "R"] : ["R", "L"]) {
      if (
        this.commitSteps([
          { move: m as "L" | "R", band: [0, CHANNEL_CLEARANCE] },
        ])
      ) {
        return true;
      }
    }
    // Inch forward along whatever corridor the guard last promised.
    return this.commitSteps(this.straightSteps(1));
  }

  // -------------------------------------------------------------------------
  // Island anchor clause
  // -------------------------------------------------------------------------

  private tryIsland(rng: Rng): boolean {
    const runLen = rng.int(5, 11);
    const margin = runLen >= 8 ? rng.int(2, 4) : rng.int(1, 3);

    // RAISED VIADUCT crossing (USER: pillars vary the island↔track
    // connection): the deck sits 0.5/1.0 BELOW the track and kit pier
    // stacks carry the channel across (assemble's structural rule —
    // abutments + piers, standing on the deck). A viaduct island is its
    // own flavor: no slope terraces, no diamond/wide/ledge furniture —
    // the stilts are the feature.
    const raised = rng.child("raised").chance(0.3)
      ? (rng.child("raised/delta").chance(0.5) ? 0.5 : 1)
      : 0;

    // The run itself: mostly straight, an occasional bend so the mass
    // isn't always a bar. First/last cells stay straight (clean shoreline
    // ports). Mass needs at least one block below every run cell.
    //
    // ISLAND VERTICALITY (M12): longer runs may embed ONE slope window
    // (0.5 or 1.0, climb or descent, from the shared vocab — terrace
    // step ≤ 0.5/cell by construction) with ≥2 flat cells on each side.
    // The mass terraces under the profile: every run cell is anchored at
    // ITS height, and the envelope follows (nearest-run-cell tops).
    interface RunSlope {
      at: number;
      option: SlopeOption;
      climb: boolean;
    }
    let slope: RunSlope | null = null;
    if (raised === 0 && runLen >= 7 && rng.child("slope").chance(0.55)) {
      const sRng = rng.child("slope/pick");
      const climb = sRng.chance(0.45);
      const delta = runLen >= 8 && sRng.chance(0.4) ? "1" : "0.5";
      const vocab = climb ? CLIMB_VOCAB : DESCENT_VOCAB;
      const options = vocab[delta].filter((o) =>
        o.totalLen <= runLen - 4 &&
        // Islands terrace at ≤0.5 per cell — no chute caps, no
        // drop-stacks (asIs single-cell falls) inside a shoreline run.
        o.parts.every((p) => p.delta / p.len <= 0.5 + 1e-9 && !p.asIs)
      );
      if (options.length > 0) {
        const option = sRng.pick(options);
        // The cubic-spillover cell (one past a climb / one before a
        // descent lip) shares the part low base, so the slope must sit
        // one cell further from the shoreline on that side to keep the
        // ≥2 flat shoreline cells contract.
        const lo = climb ? 2 : 3;
        const hi = runLen - option.totalLen - (climb ? 3 : 1);
        if (hi >= lo) {
          slope = { at: sRng.int(lo, hi), option, climb };
        }
      }
    }
    const slopeCells = new Map<number, Step>();
    if (slope) {
      const parts = slope.climb
        ? slope.option.parts
        : slope.option.parts.slice().reverse();
      let j = slope.at;
      for (const part of parts) {
        const signed = slope.climb ? part.delta : -part.delta;
        for (let k = 0; k < part.len; k++) {
          // Full part envelope per cell (same fix as commitSlope): cells
          // after the stepAfter sit at the part's exit y, so their band
          // reaches BACK across the delta. Mass headroom −1 preserved.
          const lo = (k === 0 ? Math.min(0, signed) : Math.min(0, -signed)) -
            1;
          const hi = (k === 0 ? Math.max(0, signed) : Math.max(0, -signed)) +
            CHANNEL_CLEARANCE;
          slopeCells.set(j++, {
            move: "F",
            band: [lo, hi],
            ...(k === 0 ? { stepAfter: signed } : {}),
          });
        }
      }
    }
    const steps: Step[] = [];
    for (let j = 0; j < runLen; j++) {
      const fromSlope = slopeCells.get(j);
      if (fromSlope) {
        steps.push(fromSlope);
        continue;
      }
      const edge = j === 0 || j === runLen - 1;
      const move = !edge && rng.chance(0.28)
        ? (rng.chance(0.5) ? "L" : "R")
        : "F";
      steps.push({ move, band: [-1, CHANNEL_CLEARANCE] });
    }
    const probed = this.probe(steps);
    if (!probed) return false;

    const id = this.islandCount;
    const topY = this.pose.y;
    const start = this.cells.length;
    if (!this.commitSteps(steps)) return false;
    const runCells = probed.cells.map((p) => [p.x, p.z] as const);

    // Slope reservations mirror commitSlope (parts in traversal order);
    // big island descents get their boost zone like any chute.
    if (slope) {
      const parts = slope.climb
        ? slope.option.parts
        : slope.option.parts.slice().reverse();
      let at = start + slope.at;
      for (const part of parts) {
        this.reservations.push({
          start: at,
          len: part.len,
          pieceIds: part.ids,
          reversed: !slope.climb && !part.asIs,
        });
        at += part.len;
      }
      const totalDrop = slope.option.parts.reduce((a, p) => a + p.delta, 0);
      if (!slope.climb && totalDrop >= 1.0) {
        const bStart = Math.max(start, start + slope.at - 2);
        this.mods.push({
          start: bStart,
          len: start + slope.at + slope.option.totalLen - bStart,
          kind: "boost",
        });
      }
    }

    // Terrace profile: flat cells anchor at their OWN base; each slope
    // PART's cells share that part's low base (the ramp sits on the low
    // terrace and bridges up — exactly how finite-mode ramps ground).
    const baseYs = runCells.map((_, j) => this.cells[start + j].baseY);
    const partLow = new Map<number, number>();
    if (slope) {
      const parts = slope.climb
        ? slope.option.parts
        : slope.option.parts.slice().reverse();
      let at = slope.at;
      let lastLow = 0;
      for (const part of parts) {
        const entryBase = baseYs[at];
        const low = slope.climb ? entryBase : entryBase - part.delta;
        for (let k = 0; k < part.len; k++) partLow.set(at + k, low);
        lastLow = low;
        at += part.len;
      }
      // Cubic spillover (apfuzz-58 wedge): the realized climb/chute
      // centerline is a smooth cubic that is still below the upper
      // terrace in the first cell PAST a climb (and already dipping in
      // the cell BEFORE a descent's lip). Those cells share the adjacent
      // part's low base too, or the mass face pokes up through the
      // still-sloping channel floor and walls the run.
      if (slope.climb) {
        if (at < runLen && !partLow.has(at)) partLow.set(at, lastLow);
      } else if (slope.at > 0 && !partLow.has(slope.at - 1)) {
        partLow.set(slope.at - 1, partLow.get(slope.at)!);
      }
    }
    // Raised viaduct: the whole surface (run + envelope terraces) sits
    // `raised` below the walk — the run cells' [-1, clearance] bands
    // already cover the pillar gap.
    const profile = baseYs.map((y, j) => (partLow.get(j) ?? y) - raised);

    // Split–merge diamond on the island run (M11 branch respawns): find
    // a straight stretch of ≥5 cells, check + claim both flank columns at
    // deck level, and reserve the composite diamond window. The branch
    // columns become part of the island's track patch (full mass beneath,
    // no caps, no decor; canopy skips them because their band is claimed
    // before the envelope loop below runs).
    // Maximal flat straight stretches of the run (local index, length) —
    // shared by the diamond, wide-run, and furniture pickers below.
    const stretches: [number, number][] = [];
    {
      let j = 0;
      while (j < runLen) {
        if (steps[j].move !== "F") {
          j++;
          continue;
        }
        let l = 0;
        while (j + l < runLen && steps[j + l].move === "F") l++;
        if (l >= 5) stretches.push([j, l]);
        j += l;
      }
    }
    /** True when [start, start+len) (absolute cell indices) overlaps any
     * committed reservation. */
    const overlapsReservation = (s0: number, len: number): boolean =>
      this.reservations.some((r) => r.start < s0 + len && s0 < r.start + r.len);

    const branchCells: (readonly [number, number])[] = [];
    if (raised === 0 && rng.child("diamond").chance(0.5)) {
      const dRng = rng.child("diamond/pick");
      placing:
      for (const [s0, l] of dRng.shuffle(stretches)) {
        for (let N = Math.min(3, l - 4); N >= 1; N--) {
          const total = 4 + N;
          const offsets = dRng.shuffle(
            Array.from({ length: l - total + 1 }, (_, o) => o),
          );
          for (const o of offsets) {
            const j0 = s0 + o;
            // Flat + unreserved only: a diamond can't straddle a slope
            // window (its deck is one level by construction).
            if (
              this.reservations.some((r) =>
                r.start < start + j0 + total && start + j0 < r.start + r.len
              )
            ) continue;
            const flatY = this.cells[start + j0].baseY;
            // Neighbors must share the level too, or the window's edge
            // terrace (min-of-neighbors) drops below the deck and the
            // split-double corner floats.
            if (
              (this.cells[start + j0 - 1]?.baseY ?? flatY) !== flatY ||
              (this.cells[start + j0 + total]?.baseY ?? flatY) !== flatY
            ) continue;
            const flanks: (readonly [number, number])[] = [];
            let ok = true;
            for (let k = 0; k < total && ok; k++) {
              const cell = this.cells[start + j0 + k];
              if (cell.baseY !== flatY) {
                ok = false;
                break;
              }
              for (
                const side of [turnLeft(cell.dirIn), turnRight(cell.dirIn)]
              ) {
                const [dx, dz] = DIR_STEP[side];
                const fx = cell.x + dx;
                const fz = cell.z + dz;
                if (
                  runCells.some(([rx, rz]) => rx === fx && rz === fz) ||
                  !this.occ.isFree(fx, fz, flatY, flatY + CHANNEL_CLEARANCE)
                ) {
                  ok = false;
                  break;
                }
                flanks.push([fx, fz]);
              }
            }
            if (!ok) continue;
            for (const [fx, fz] of flanks) {
              this.occ.claim(
                fx,
                fz,
                flatY,
                flatY + CHANNEL_CLEARANCE,
                this.ref,
              );
            }
            branchCells.push(...flanks);
            this.reservations.push({
              start: start + j0,
              len: total,
              pieceIds: [`diamond-${N}`],
              reversed: false,
            });
            break placing;
          }
        }
      }
    }

    // WIDE RUN (ground vocabulary — an island top IS local ground): a
    // funnel-bracketed wide channel on a flat straight stretch, exactly
    // the loop-mode feature shape (funnel → wide×1..3 → funnel reversed).
    // Both flank columns of every window cell must be free (the wide
    // profile bulges 0.35 into them); they're claimed as channel band and
    // become track patch (full mass, no caps/decor) like branch columns.
    const wideFlanks: (readonly [number, number])[] = [];
    if (raised === 0 && rng.child("wide").chance(0.4)) {
      const wRng = rng.child("wide/pick");
      placingWide:
      for (const [s0, l] of wRng.shuffle(stretches)) {
        for (let mid = Math.min(3, l - 2); mid >= 1; mid--) {
          const total = mid + 2;
          const offsets = wRng.shuffle(
            Array.from({ length: l - total + 1 }, (_, o) => o),
          );
          for (const o of offsets) {
            const j0 = s0 + o;
            if (overlapsReservation(start + j0, total)) continue;
            const flatY = this.cells[start + j0].baseY;
            let ok = true;
            const flanks: (readonly [number, number])[] = [];
            for (let k = 0; k < total && ok; k++) {
              const cell = this.cells[start + j0 + k];
              if (cell.baseY !== flatY || cell.baseY !== profile[j0 + k]) {
                ok = false;
                break;
              }
              for (
                const side of [turnLeft(cell.dirIn), turnRight(cell.dirIn)]
              ) {
                const [dx, dz] = DIR_STEP[side];
                const fx = cell.x + dx;
                const fz = cell.z + dz;
                if (
                  runCells.some(([rx, rz]) => rx === fx && rz === fz) ||
                  !this.occ.isFree(fx, fz, flatY, flatY + CHANNEL_CLEARANCE)
                ) {
                  ok = false;
                  break;
                }
                flanks.push([fx, fz]);
              }
            }
            if (!ok) continue;
            for (const [fx, fz] of flanks) {
              this.occ.claim(
                fx,
                fz,
                flatY,
                flatY + CHANNEL_CLEARANCE,
                this.ref,
              );
            }
            wideFlanks.push(...flanks);
            this.reservations.push({
              start: start + j0,
              len: 1,
              pieceIds: ["funnel"],
              reversed: false,
            });
            for (let k = 0; k < mid; k++) {
              this.reservations.push({
                start: start + j0 + 1 + k,
                len: 1,
                pieceIds: ["straight-wide"],
                reversed: false,
              });
            }
            this.reservations.push({
              start: start + j0 + 1 + mid,
              len: 1,
              pieceIds: ["funnel"],
              reversed: true,
            });
            break placingWide;
          }
        }
      }
    }

    // FURNITURE LEDGES: 1–3 single cells flanking flat straight run cells
    // — claimed now (mass beneath, no canopy), furnished after realize
    // (stub siding or fan) if their host realizes as a plain straight.
    const sideCells: {
      cell: readonly [number, number];
      host: number;
      side: "L" | "R";
    }[] = [];
    if (raised === 0 && rng.child("furniture").chance(0.55)) {
      const fRng = rng.child("furniture/pick");
      const candidates: typeof sideCells = [];
      for (let j = 1; j < runLen - 1; j++) {
        const cell = this.cells[start + j];
        if (
          cell.move !== "F" || cell.baseY !== profile[j] ||
          overlapsReservation(start + j, 1)
        ) continue;
        for (
          const [side, dir] of [
            ["L", turnLeft(cell.dirIn)],
            ["R", turnRight(cell.dirIn)],
          ] as const
        ) {
          const [dx, dz] = DIR_STEP[dir];
          const fx = cell.x + dx;
          const fz = cell.z + dz;
          if (
            runCells.some(([rx, rz]) => rx === fx && rz === fz) ||
            !this.occ.isFree(
              fx,
              fz,
              cell.baseY,
              cell.baseY + CHANNEL_CLEARANCE,
            )
          ) continue;
          candidates.push({ cell: [fx, fz], host: start + j, side });
        }
      }
      const want = fRng.int(1, 4);
      const usedHosts = new Set<number>();
      const usedCells = new Set<string>();
      for (const cand of fRng.shuffle(candidates)) {
        if (sideCells.length >= want) break;
        if (usedHosts.has(cand.host)) continue;
        if (usedCells.has(key(cand.cell[0], cand.cell[1]))) continue;
        this.occ.claim(
          cand.cell[0],
          cand.cell[1],
          this.cells[cand.host].baseY,
          this.cells[cand.host].baseY + CHANNEL_CLEARANCE,
          this.ref,
        );
        usedHosts.add(cand.host);
        usedCells.add(key(cand.cell[0], cand.cell[1]));
        sideCells.push(cand);
      }
    }

    // Claim the mass envelope now — blob rings around the run, each cell
    // reserving as much depth below topY as older claims allow. The island
    // builder works strictly inside `isle/${id}` claims.
    const isleRef = `isle/${id}`;
    const envelope = new Map<string, [number, number]>();
    for (const [cx, cz] of runCells) {
      for (let dx = -margin; dx <= margin; dx++) {
        for (let dz = -margin; dz <= margin; dz++) {
          envelope.set(key(cx + dx, cz + dz), [cx + dx, cz + dz]);
        }
      }
    }
    // Approach/departure corridors stay canopy-free: the line must be able
    // to arrive and leave at surface height, and the promise the run's
    // pocket guard made about the exit must survive the envelope claims.
    // (Bonus: a natural clearing where the track crosses the shoreline.)
    const corridor = new Set<string>();
    {
      const exit = this.pose; // commitSteps already advanced past the run
      const entry = this.cells[start];
      for (let k = 0; k <= margin + 2; k++) {
        corridor.add(
          key(
            exit.x + DIR_STEP[exit.dir][0] * k,
            exit.z + DIR_STEP[exit.dir][1] * k,
          ),
        );
        corridor.add(
          key(
            entry.x - DIR_STEP[entry.dirIn][0] * k,
            entry.z - DIR_STEP[entry.dirIn][1] * k,
          ),
        );
      }
    }
    // Terrace tops for the envelope: each cell takes the floor base of
    // its NEAREST run cell (Chebyshev; ties pick the lower top — a low
    // terrace beside the channel can never intrude into it). Flat runs
    // reduce to the old constant-top behavior exactly.
    const terraceTop = (cx: number, cz: number): number => {
      let best = Infinity;
      let top = topY;
      for (let j = 0; j < runCells.length; j++) {
        const [rx, rz] = runCells[j];
        const d = Math.max(Math.abs(cx - rx), Math.abs(cz - rz));
        if (d < best || (d === best && profile[j] < top)) {
          best = d;
          top = profile[j];
        }
      }
      return top;
    };
    // Own-segment bands are transparent ONLY on the run's own cells (and
    // a diamond's branch columns) — their channel claims carry the −1
    // mass-headroom band the envelope must fill. Everywhere else a
    // same-segment channel (the island's own approach corridor!) is as
    // solid as any older line: skipping it let terraced mass plow
    // through the corridor (det-seed jammed a marble in it).
    const ownCells = new Set([
      ...runCells.map(([x, z]) => key(x, z)),
      ...branchCells.map(([x, z]) => key(x, z)),
    ]);
    const deckCells: (readonly [number, number, number])[] = [];
    for (const [cx, cz] of envelope.values()) {
      const cellTop = terraceTop(cx, cz);
      // Deepest free band under the top: stop at the first older claim.
      let floor = cellTop - ISLAND_MAX_DEPTH;
      for (const b of this.occ.at(cx, cz)) {
        if (b.ref === isleRef) continue;
        if (b.ref === this.ref && ownCells.has(key(cx, cz))) continue;
        if (b.y1 <= cellTop + 1e-9 && b.y1 > floor) floor = b.y1;
        // A band overlapping [floor, cellTop) from above (old track
        // between floor and top) — mass can't reach the top here; skip.
        if (b.y0 < cellTop - 1e-9 && b.y1 > cellTop - 1e-9) {
          floor = cellTop;
        }
      }
      if (cellTop - floor < 0.999) continue; // no room for even one block
      this.occ.claim(cx, cz, floor, cellTop, isleRef);
      deckCells.push([cx, cz, cellTop]);
      // Canopy airspace: non-run surface cells may grow tall decor (trees,
      // banners); claim the air above them so no future line threads
      // through a crown. Cells whose airspace is already spoken for simply
      // stay vegetation-free (the builder checks for this claim).
      if (
        !runCells.some(([rx, rz]) => rx === cx && rz === cz) &&
        !corridor.has(key(cx, cz)) &&
        this.occ.isFree(cx, cz, cellTop, cellTop + CANOPY_HEIGHT)
      ) {
        this.occ.claim(cx, cz, cellTop, cellTop + CANOPY_HEIGHT, isleRef);
      }
    }

    this.crossings.push({
      id,
      start,
      len: runLen,
      topY,
      profile,
      margin,
      ...(raised > 0 ? { raised } : {}),
      ...(branchCells.length > 0 ? { branchCells } : {}),
      ...(wideFlanks.length > 0 ? { wideFlanks } : {}),
      ...(sideCells.length > 0 ? { sideCells } : {}),
      ...(deckCells.length > 0 ? { deckCells } : {}),
    });
    this.islandCount++;
    this.sinceAnchor = 0;
    this.anchorEvery = rng.int(ANCHOR_EVERY_MIN, ANCHOR_EVERY_MAX);
    return true;
  }
}
